import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { Chess } from 'chess.js';
import { useSearchParams } from 'react-router-dom';
import { useDestrierTheme } from '../ThemeContext';
import PunishmentPanel from './PunishmentPanel';
import OpeningTrainerPanel from './OpeningTrainerPanel';
import FindCheckmatePanel from './FindCheckmatePanel';
import BlunderReviewPanel from './BlunderReviewPanel';
import InteractiveBoard, { svgPieceUrl, playSound, getSoundType } from './InteractiveBoard';
import GameAnalysisPanel from './GameAnalysisPanel';
import useStockfish from '../hooks/useStockfish';

const TRAINING_MODES = [
  {
    key: 'free',
    label: 'Free Study',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <line x1="3" y1="12" x2="21" y2="12" />
        <line x1="12" y1="3" x2="12" y2="21" />
      </svg>
    ),
    description: 'Your personal chess laboratory. Load any position, play through openings, or just experiment freely.',
  },
  {
    key: 'analysis',
    label: 'Game Analysis',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
      </svg>
    ),
    description: 'Review any of your chess.com games move by move. Stockfish runs in your browser to find blunders, mistakes, and brilliant moves.',
  },
  {
    key: 'openings',
    label: 'Opening Trainer',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 19.5A2.5 2.5 0 016.5 17H20" />
        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z" />
        <line x1="9" y1="7" x2="16" y2="7" />
        <line x1="9" y1="11" x2="14" y2="11" />
      </svg>
    ),
    description: 'Drill your opening repertoire. Practice the critical lines from your games and build muscle memory for the moves that matter.',
  },
  {
    key: 'blunders',
    label: 'Blunder Review',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
    ),
    description: 'Replay your actual blunders from recent games. Find the right move you missed and train yourself not to repeat the same mistakes.',
  },
  {
    key: 'punishment',
    label: 'Punish Mistakes',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" />
        <path d="M15 9l-6 6M9 9l6 6" />
      </svg>
    ),
    description: 'Your opponent just blundered. Can you find the strongest punishment? Train the killer instinct to make them pay for every mistake.',
  },
  {
    key: 'checkmate',
    label: 'Find Checkmate',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
        <circle cx="12" cy="12" r="4" />
      </svg>
    ),
    description: 'You had a forced checkmate but missed it. Can you find it now? Train your pattern recognition to never miss a winning combination again.',
  },
  {
    key: 'endgames',
    label: 'Endgames',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2L2 7l10 5 10-5-10-5z" />
        <path d="M2 17l10 5 10-5" />
      </svg>
    ),
    description: 'Practice essential endgame patterns — king and pawn, rook endings, opposite-color bishops. The positions where most Elo is won and lost.',
  },
];

export default function TrainingRoomPage() {
  const { c } = useDestrierTheme();
  const [searchParams] = useSearchParams();
  const openingName = searchParams.get('opening') || '';
  const initialMoves = searchParams.get('moves') || '';
  const [activeMode, setActiveMode] = useState(openingName ? 'openings' : 'free');

  const [chess] = useState(() => new Chess());
  const [history, setHistory] = useState([]);
  const [moveIndex, setMoveIndex] = useState(0);
  const [positions, setPositions] = useState([]);
  const [flipped, setFlipped] = useState(false);
  const [selectedSquare, setSelectedSquare] = useState(null);
  const [legalMoves, setLegalMoves] = useState([]);
  const [fenInput, setFenInput] = useState(''); // eslint-disable-line no-unused-vars
  const [lastMove, setLastMove] = useState(null);
  const [pieceSet, setPieceSet] = useState('cburnett');
  const [boardTheme, setBoardTheme] = useState('amethyst');
  const [showHeatmap, setShowHeatmap] = useState(false);
  const [showThreats, setShowThreats] = useState(false);
  const [showEngine, setShowEngine] = useState(true);
  const [dragFrom, setDragFrom] = useState(null);
  const [dragPos, setDragPos] = useState(null);
  const [dragPiece, setDragPiece] = useState(null);
  const [hoveredSquare, setHoveredSquare] = useState(null);
  const [arrows, setArrows] = useState([]);
  const [highlightedSquares, setHighlightedSquares] = useState(new Set());
  const [pendingPromotion, setPendingPromotion] = useState(null); // { from, to, color }
  const boardRef = useRef(null);

  useEffect(() => {
    if (initialMoves) {
      const tempChess = new Chess();
      const moves = initialMoves.split(/\s+/).filter(m => m && !m.match(/^\d+\./));
      const pos = [tempChess.fen()];
      const hist = [];
      for (const move of moves) {
        try {
          const result = tempChess.move(move);
          if (result) { hist.push(result); pos.push(tempChess.fen()); }
        } catch { break; }
      }
      chess.load(pos[pos.length - 1]);
      setHistory(hist);
      setPositions(pos);
      setMoveIndex(pos.length - 1);
      if (hist.length > 0) { const last = hist[hist.length - 1]; setLastMove({ from: last.from, to: last.to }); }
    } else {
      setPositions([chess.fen()]);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const currentFen = positions[moveIndex] || chess.fen();
  const boardChess = useMemo(() => { const bc = new Chess(); bc.load(currentFen); return bc; }, [currentFen]);

  // History-aware Chess instance for draw detection (threefold repetition, 50-move rule)
  const gameChess = useMemo(() => {
    const gc = new Chess();
    for (let i = 0; i < Math.min(moveIndex, history.length); i++) {
      try { gc.move(history[i].san); } catch { break; }
    }
    return gc;
  }, [history, moveIndex]);

  const squareName = useCallback((ri, ci) => {
    const f = flipped ? 7 - ci : ci;
    const r = flipped ? ri : 7 - ri;
    return 'abcdefgh'[f] + (r + 1);
  }, [flipped]);

  // ── Drag-and-drop ──
  const getSquareFromPoint = useCallback((clientX, clientY) => {
    if (!boardRef.current) return null;
    const rect = boardRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    if (x < 0 || y < 0 || x >= rect.width || y >= rect.height) return null;
    const col = Math.floor((x / rect.width) * 8);
    const row = Math.floor((y / rect.height) * 8);
    const f = flipped ? 7 - col : col;
    const r = flipped ? row : 7 - row;
    return 'abcdefgh'[f] + (r + 1);
  }, [flipped]);

  const handleDragStart = useCallback((ri, ci, e) => {
    if (moveIndex !== positions.length - 1) return;
    const sq = squareName(ri, ci);
    const piece = boardChess.get(sq);
    if (!piece || piece.color !== boardChess.turn()) return;
    e.preventDefault();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    setDragFrom(sq);
    setDragPiece(piece);
    setDragPos({ x: clientX, y: clientY });
    setSelectedSquare(sq);
    setLegalMoves(boardChess.moves({ square: sq, verbose: true }).map(m => m.to));
  }, [moveIndex, positions.length, squareName, boardChess]);

  // Check if a move is a promotion
  const isPromotion = useCallback((from, to) => {
    const piece = boardChess.get(from);
    if (!piece || piece.type !== 'p') return false;
    const destRank = to[1];
    return (piece.color === 'w' && destRank === '8') || (piece.color === 'b' && destRank === '1');
  }, [boardChess]);

  // Execute a move with a specific promotion piece
  const executeMove = useCallback((from, to, promotion = 'q') => {
    try {
      const tempChess = new Chess(currentFen);
      const result = tempChess.move({ from, to, promotion });
      if (result) {
        const newHistory = [...history.slice(0, moveIndex), result];
        const newPositions = [...positions.slice(0, moveIndex + 1), tempChess.fen()];
        chess.load(tempChess.fen());
        setHistory(newHistory);
        setPositions(newPositions);
        setMoveIndex(newPositions.length - 1);
        setLastMove({ from: result.from, to: result.to });
        setSelectedSquare(null);
        setLegalMoves([]);
        playSound(getSoundType(result));
        return true;
      }
    } catch { /* invalid move */ }
    return false;
  }, [currentFen, chess, history, positions, moveIndex]);

  // Handle promotion choice
  const handlePromotionChoice = useCallback((pieceType) => {
    if (!pendingPromotion) return;
    executeMove(pendingPromotion.from, pendingPromotion.to, pieceType);
    setPendingPromotion(null);
  }, [pendingPromotion, executeMove]);

  useEffect(() => {
    if (!dragFrom) return;
    const onMove = (e) => {
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      setDragPos({ x: clientX, y: clientY });
    };
    const onEnd = (e) => {
      const clientX = e.changedTouches ? e.changedTouches[0].clientX : e.clientX;
      const clientY = e.changedTouches ? e.changedTouches[0].clientY : e.clientY;
      const dropSq = getSquareFromPoint(clientX, clientY);
      if (dropSq && dropSq !== dragFrom) {
        if (isPromotion(dragFrom, dropSq)) {
          const piece = boardChess.get(dragFrom);
          setPendingPromotion({ from: dragFrom, to: dropSq, color: piece.color });
          setDragFrom(null); setDragPiece(null); setDragPos(null);
          return;
        }
        executeMove(dragFrom, dropSq);
      }
      setDragFrom(null); setDragPiece(null); setDragPos(null);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onEnd);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onEnd);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onEnd);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
    };
  }, [dragFrom, currentFen, chess, history, positions, moveIndex, getSquareFromPoint, boardChess, executeMove, isPromotion]);

  const handleSquareClick = useCallback((ri, ci) => {
    if (moveIndex !== positions.length - 1) return;
    const sq = squareName(ri, ci);
    const piece = boardChess.get(sq);
    if (selectedSquare) {
      // Check if this is a valid move target
      const legalForSelected = boardChess.moves({ square: selectedSquare, verbose: true });
      const isValidTarget = legalForSelected.some(m => m.to === sq);
      if (isValidTarget) {
        if (isPromotion(selectedSquare, sq)) {
          const p = boardChess.get(selectedSquare);
          setPendingPromotion({ from: selectedSquare, to: sq, color: p.color });
          return;
        }
        executeMove(selectedSquare, sq);
        return;
      }
      if (piece && piece.color === boardChess.turn()) {
        setSelectedSquare(sq);
        setLegalMoves(boardChess.moves({ square: sq, verbose: true }).map(m => m.to));
        return;
      }
      setSelectedSquare(null);
      setLegalMoves([]);
      return;
    }
    if (piece && piece.color === boardChess.turn()) {
      setSelectedSquare(sq);
      setLegalMoves(boardChess.moves({ square: sq, verbose: true }).map(m => m.to));
    }
  }, [selectedSquare, boardChess, positions, moveIndex, squareName, executeMove, isPromotion]);

  const goTo = useCallback((idx) => {
    const clamped = Math.max(0, Math.min(idx, positions.length - 1));
    setMoveIndex(clamped);
    setSelectedSquare(null);
    setLegalMoves([]);
    if (clamped > 0 && history[clamped - 1]) { setLastMove({ from: history[clamped - 1].from, to: history[clamped - 1].to }); }
    else { setLastMove(null); }
  }, [positions, history]);

  const handleReset = useCallback(() => { // eslint-disable-line no-unused-vars
    chess.reset(); setHistory([]); setPositions([chess.fen()]); setMoveIndex(0); setSelectedSquare(null); setLegalMoves([]); setLastMove(null);
  }, [chess]);

  const handleLoadFen = useCallback(() => { // eslint-disable-line no-unused-vars
    try { chess.load(fenInput); setHistory([]); setPositions([chess.fen()]); setMoveIndex(0); setSelectedSquare(null); setLegalMoves([]); setLastMove(null); }
    catch { /* invalid FEN */ }
  }, [chess, fenInput]);

  const handleUndo = useCallback(() => { // eslint-disable-line no-unused-vars
    if (moveIndex <= 0) return;
    const newHistory = history.slice(0, moveIndex - 1);
    const newPositions = positions.slice(0, moveIndex);
    chess.load(newPositions[newPositions.length - 1]);
    setHistory(newHistory); setPositions(newPositions); setMoveIndex(newPositions.length - 1);
    setSelectedSquare(null); setLegalMoves([]);
    if (newHistory.length > 0) { const last = newHistory[newHistory.length - 1]; setLastMove({ from: last.from, to: last.to }); }
    else { setLastMove(null); }
  }, [chess, history, positions, moveIndex]);

  const movePairs = [];
  for (let i = 0; i < history.length; i += 2) {
    movePairs.push({ num: Math.floor(i / 2) + 1, white: history[i], black: history[i + 1] || null, whiteIdx: i + 1, blackIdx: i + 2 });
  }

  // ── Derived data for right panel ──
  const turnColor = boardChess.turn(); // 'w' or 'b'
  const fullMoveNumber = Math.floor(moveIndex / 2) + 1;
  const isCheck = boardChess.isCheck();
  const isCheckmate = boardChess.isCheckmate();
  const isStalemate = boardChess.isStalemate();
  const isThreefoldRepetition = gameChess.isThreefoldRepetition();
  const isInsufficientMaterial = boardChess.isInsufficientMaterial();
  const isFiftyMoveRule = gameChess.isDraw() && !isStalemate && !isInsufficientMaterial && !isThreefoldRepetition;
  const isDraw = isStalemate || isThreefoldRepetition || isInsufficientMaterial || isFiftyMoveRule;
  const isGameOver = isCheckmate || isDraw;

  // Material count
  const materialCount = useMemo(() => {
    const values = { p: 1, n: 3, b: 3, r: 5, q: 9 };
    const pieces = { w: { p: 0, n: 0, b: 0, r: 0, q: 0 }, b: { p: 0, n: 0, b: 0, r: 0, q: 0 } };
    const startPieces = { p: 8, n: 2, b: 2, r: 2, q: 1 };
    for (const row of boardChess.board()) {
      for (const sq of row) {
        if (sq && sq.type !== 'k') pieces[sq.color][sq.type]++;
      }
    }
    const captured = { w: {}, b: {} };
    for (const type of ['q', 'r', 'b', 'n', 'p']) {
      captured.w[type] = Math.max(0, startPieces[type] - pieces.b[type]);
      captured.b[type] = Math.max(0, startPieces[type] - pieces.w[type]);
    }
    let wMat = 0, bMat = 0;
    for (const t of ['p', 'n', 'b', 'r', 'q']) { wMat += pieces.w[t] * values[t]; bMat += pieces.b[t] * values[t]; }
    return { pieces, captured, advantage: wMat - bMat, wMat, bMat };
  }, [currentFen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Opening detection
  const detectedOpening = useMemo(() => {
    const openings = [
      { moves: 'e4 e5 Nf3 Nc6 Bb5', name: 'Ruy Lopez' },
      { moves: 'e4 e5 Nf3 Nc6 Bc4', name: 'Italian Game' },
      { moves: 'e4 e5 Nf3 Nc6 Bc4 Bc5', name: 'Giuoco Piano' },
      { moves: 'e4 e5 Nf3 Nc6 Bc4 Nf6', name: 'Two Knights Defense' },
      { moves: 'e4 e5 Nf3 Nc6 d4', name: 'Scotch Game' },
      { moves: 'e4 e5 Nf3 Nf6', name: "Petrov's Defense" },
      { moves: 'e4 c5', name: 'Sicilian Defense' },
      { moves: 'e4 c5 Nf3 d6 d4 cxd4 Nxd4 Nf6 Nc3 a6', name: 'Sicilian Najdorf' },
      { moves: 'e4 c5 Nf3 d6 d4 cxd4 Nxd4 Nf6 Nc3 g6', name: 'Sicilian Dragon' },
      { moves: 'e4 c5 Nf3 Nc6', name: 'Sicilian (Open)' },
      { moves: 'e4 e6', name: 'French Defense' },
      { moves: 'e4 e6 d4 d5 Nc3', name: 'French Winawer' },
      { moves: 'e4 c6', name: 'Caro-Kann Defense' },
      { moves: 'e4 c6 d4 d5 Nc3 dxe4 Nxe4', name: 'Caro-Kann Classical' },
      { moves: 'e4 d5', name: 'Scandinavian Defense' },
      { moves: 'e4 d6 d4 Nf6 Nc3 g6', name: "Pirc Defense" },
      { moves: 'e4 g6', name: 'Modern Defense' },
      { moves: 'd4 d5 c4', name: "Queen's Gambit" },
      { moves: 'd4 d5 c4 e6', name: "Queen's Gambit Declined" },
      { moves: 'd4 d5 c4 dxc4', name: "Queen's Gambit Accepted" },
      { moves: 'd4 d5 c4 c6', name: 'Slav Defense' },
      { moves: 'd4 Nf6 c4 g6 Nc3 Bg7', name: "King's Indian Defense" },
      { moves: 'd4 Nf6 c4 e6 Nc3 Bb4', name: 'Nimzo-Indian Defense' },
      { moves: 'd4 Nf6 c4 e6 Nf3 b6', name: "Queen's Indian Defense" },
      { moves: 'd4 Nf6 c4 c5', name: 'Benoni Defense' },
      { moves: 'd4 f5', name: 'Dutch Defense' },
      { moves: 'd4 d5 Bf4', name: 'London System' },
      { moves: 'd4 d5 Nf3 Nf6 Bf4', name: 'London System' },
      { moves: 'Nf3 d5 g3', name: "King's Indian Attack" },
      { moves: 'c4', name: 'English Opening' },
      { moves: 'c4 e5', name: 'English (Reversed Sicilian)' },
      { moves: 'Nf3 Nf6 g3', name: 'Reti Opening' },
      { moves: 'e4 e5 f4', name: "King's Gambit" },
      { moves: 'e4 e5 Nc3', name: 'Vienna Game' },
      { moves: 'd4 Nf6 c4 g6 Nc3 d5', name: 'Grunfeld Defense' },
    ];
    const moveStr = history.slice(0, moveIndex).map(m => m.san).join(' ');
    let best = null;
    for (const op of openings) {
      if (moveStr.startsWith(op.moves) && (!best || op.moves.length > best.moves.length)) {
        best = op;
      }
    }
    return best?.name || (history.length > 0 ? 'Starting Position' : null);
  }, [history, moveIndex]);

  // Browser-side Stockfish eval for Free Study mode
  const { evaluatePosition: sfEvaluate, stop: sfStop, ready: sfReady } = useStockfish(16);
  const [sfEval, setSfEval] = useState(null); // { cp, bestMove }
  const [sfLoading, setSfLoading] = useState(false);

  useEffect(() => {
    if (activeMode !== 'free' || !sfReady || !showEngine) {
      setSfEval(null);
      return;
    }
    const fen = currentFen;
    if (!fen) return;

    setSfLoading(true);
    let cancelled = false;

    sfEvaluate(fen).then((result) => {
      if (cancelled || !result) return;
      // cp from engine is side-to-move perspective; convert to white's perspective
      let cp = result.cp;
      if (fen.includes(' b ')) cp = -cp;

      // Convert UCI move to SAN
      let bestMove = result.bestMoveUci;
      try {
        const tmp = new Chess(fen);
        const moveObj = tmp.move({ from: bestMove.slice(0, 2), to: bestMove.slice(2, 4), promotion: bestMove[4] || undefined });
        if (moveObj) bestMove = moveObj.san;
      } catch (_) {}

      setSfEval({ cp, bestMove, bestMoveUci: result.bestMoveUci });
      setSfLoading(false);
    });

    return () => { cancelled = true; sfStop(); };
  }, [currentFen, activeMode, sfReady, sfEvaluate, sfStop, showEngine]);

  // Eval bar: use Stockfish cp when available, fall back to material
  // cp from backend is always from white's perspective; flip when board is flipped
  const evalPercent = useMemo(() => {
    if (sfEval && sfEval.cp !== undefined) {
      const cp = flipped ? -sfEval.cp : sfEval.cp;
      // Sigmoid: 50 + 50 * (2 / (1 + e^(-cp/300)) - 1)
      const sigmoid = 50 + 50 * (2 / (1 + Math.exp(-cp / 300)) - 1);
      return Math.max(2, Math.min(98, Math.round(sigmoid)));
    }
    const adv = flipped ? -materialCount.advantage : materialCount.advantage;
    return Math.max(5, Math.min(95, 50 + adv * 5));
  }, [sfEval, materialCount.advantage, flipped]);

  // Format eval for display (from bottom player's perspective)
  const evalDisplay = useMemo(() => {
    if (!sfEval || sfEval.cp === undefined) {
      const adv = flipped ? -materialCount.advantage : materialCount.advantage;
      return adv;
    }
    const cp = flipped ? -sfEval.cp : sfEval.cp;
    if (Math.abs(cp) >= 10000) return cp > 0 ? 'M' : '-M';
    return (cp / 100).toFixed(1);
  }, [sfEval, materialCount.advantage, flipped]);

  // The color on the bottom of the board
  const playerColor = flipped ? 'b' : 'w';

  // Heatmap: show squares controlled by the bottom-side player only
  const heatmapData = useMemo(() => {
    if (!showHeatmap) return null;
    const map = {};
    const squares = [];
    for (const f of 'abcdefgh') for (const r of '12345678') squares.push(f + r);
    for (const sq of squares) map[sq] = 0;
    for (const sq of squares) {
      const piece = boardChess.get(sq);
      if (!piece || piece.color !== playerColor) continue;
      try {
        const moves = boardChess.moves({ square: sq, verbose: true });
        for (const m of moves) map[m.to]++;
      } catch { /* skip */ }
    }
    return map;
  }, [currentFen, showHeatmap, playerColor]); // eslint-disable-line react-hooks/exhaustive-deps

  // Threat detection: only show threats to the bottom-side player's pieces
  const threatData = useMemo(() => {
    if (!showThreats) return null;
    const threats = {};
    const opponent = playerColor === 'w' ? 'b' : 'w';
    const squares = [];
    for (const f of 'abcdefgh') for (const r of '12345678') squares.push(f + r);
    for (const sq of squares) {
      const piece = boardChess.get(sq);
      if (!piece || piece.color !== playerColor) continue;
      if (boardChess.isAttacked(sq, opponent)) {
        const isDefended = boardChess.isAttacked(sq, playerColor);
        threats[sq] = { attacked: true, defended: isDefended, piece };
      }
    }
    return threats;
  }, [currentFen, showThreats, playerColor]); // eslint-disable-line react-hooks/exhaustive-deps

  // Move classification
  const classifyMove = useCallback((move) => {
    const tags = [];
    if (move.san === 'O-O' || move.san === 'O-O-O') { tags.push('Castle'); return tags; }
    if (move.san.includes('+')) tags.push('Check');
    if (move.san.includes('#')) { tags.push('Checkmate'); return tags; }
    if (move.captured) tags.push('Capture');
    if (move.piece === 'p' && (move.to[1] === '8' || move.to[1] === '1')) tags.push('Promotion');
    if (move.piece !== 'p' && !move.captured && moveIndex <= 20) {
      const devPieces = ['n', 'b'];
      if (devPieces.includes(move.piece)) tags.push('Development');
    }
    if (move.piece === 'p') tags.push('Pawn push');
    if (tags.length === 0) tags.push('Quiet');
    return tags;
  }, [moveIndex]);

  // PGN export
  const generatePGN = useCallback(() => {
    const moves = history.slice(0, moveIndex).map((m, i) => {
      const num = Math.floor(i / 2) + 1;
      return i % 2 === 0 ? `${num}. ${m.san}` : m.san;
    }).join(' ');
    const pgn = `[Event "Training Rooms"]\n[Date "${new Date().toISOString().slice(0, 10)}"]\n[White "?"]\n[Black "?"]\n[Result "*"]\n\n${moves} *`;
    navigator.clipboard.writeText(pgn);
    return pgn;
  }, [history, moveIndex]);

  const [pgnCopied, setPgnCopied] = useState(false);

  const currentMode = TRAINING_MODES.find(m => m.key === activeMode) || TRAINING_MODES[0];

  return (
    <Box sx={{ minHeight: '100vh', background: c.pageBg || c.bg, display: 'flex' }}>

      {/* ── Secondary sidebar (desktop) ── */}
      <Box sx={{
        display: { xs: 'none', md: 'flex' },
        flexDirection: 'column',
        width: 200,
        borderRight: `1px solid ${c.textMuted}10`,
        bgcolor: c.mode === 'dark' ? '#1a1a1e' : '#f5f0eb',
        position: 'fixed', left: 64, top: 60,
        height: 'calc(100vh - 60px)',
        zIndex: 40,
        overflowY: 'auto',
        pt: 2,
      }}>
        <Typography sx={{ color: c.textMuted, fontSize: '0.8rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', px: 2, mb: 1.5 }}>
          Training Rooms
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, px: 1 }}>
          {TRAINING_MODES.map((mode) => {
            const isActive = activeMode === mode.key;
            const isComingSoon = mode.key === 'endgames';
            return (
              <Box key={mode.key} onClick={isComingSoon ? undefined : () => setActiveMode(mode.key)} sx={{
                display: 'flex', alignItems: 'center', gap: 1.5, position: 'relative',
                height: 44, px: 1.25, borderRadius: '12px',
                cursor: isComingSoon ? 'default' : 'pointer',
                bgcolor: isActive ? `${c.accent}15` : 'transparent',
                color: isActive ? c.accent : c.textMuted,
                transition: 'none',
                '&:hover': isComingSoon ? {} : { bgcolor: isActive ? `${c.accent}20` : `${c.textMuted}10`, color: isActive ? c.accent : c.textDark },
              }}>
                <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', width: 22, opacity: isComingSoon ? 0.6 : 1 }}>{mode.icon}</Box>
                <Typography sx={{ fontSize: '0.88rem', fontWeight: isActive ? 600 : 500, whiteSpace: 'nowrap', overflow: 'hidden', opacity: isComingSoon ? 0.6 : 1 }}>
                  {mode.label}
                </Typography>
                {isComingSoon && (
                  <Box sx={{ px: 0.8, py: 0.15, borderRadius: '6px', bgcolor: `${c.accent}15`, ml: 'auto' }}>
                    <Typography sx={{ color: c.accent, fontSize: '0.55rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Soon</Typography>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* ── Main content ── */}
      <Box sx={{ flex: 1, minWidth: 0, ml: { xs: 0, md: '96px' }, mr: { xs: 0, md: '30px' } }}>

        {/* Mobile mode selector (horizontal scroll) */}
        <Box sx={{
          display: { xs: 'flex', md: 'none' },
          gap: 0.5, px: 2, pt: 9, pb: 1,
          overflowX: 'auto',
          '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none',
        }}>
          {TRAINING_MODES.map((mode) => {
            const isActive = activeMode === mode.key;
            const isComingSoon = mode.key === 'endgames';
            return (
              <Box key={mode.key} onClick={isComingSoon ? undefined : () => setActiveMode(mode.key)} sx={{
                display: 'flex', alignItems: 'center', gap: 0.8,
                px: 1.5, py: 0.8, borderRadius: '10px',
                cursor: isComingSoon ? 'default' : 'pointer', flexShrink: 0,
                bgcolor: isActive ? `${c.accent}15` : `${c.textMuted}06`,
                border: `1.5px solid ${isActive ? c.accent : 'transparent'}`,
                color: isActive ? c.accent : c.textMuted, transition: 'all 0.2s ease',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, opacity: isComingSoon ? 0.6 : 1 }}>{mode.icon}</Box>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: isActive ? 700 : 600, whiteSpace: 'nowrap', opacity: isComingSoon ? 0.6 : 1 }}>{mode.label}</Typography>
                {isComingSoon && (
                  <Box sx={{ px: 0.6, py: 0.1, borderRadius: '5px', bgcolor: `${c.accent}15` }}>
                    <Typography sx={{ color: c.accent, fontSize: '0.5rem', fontWeight: 700, textTransform: 'uppercase' }}>Soon</Typography>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>

        <Box sx={{ px: { xs: 1, md: 0 }, pb: 6, pt: { xs: 2, md: 10 } }}>

          <Box sx={{ mb: 3 }}>
            <Typography sx={{ color: c.textDark, fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.6rem' }, fontWeight: 900, letterSpacing: '-0.03em', mb: 1 }}>
              {currentMode.label}
            </Typography>
            <Typography sx={{ color: c.textMuted, fontSize: { xs: '1.05rem', md: '1.15rem' }, lineHeight: 1.7 }}>
              {currentMode.description}
            </Typography>
          </Box>

          {/* ── FREE BOARD mode ── */}
          {activeMode === 'free' && (
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 3, alignItems: { lg: 'flex-start' } }}>
            <Box sx={{ flexShrink: 0, position: 'relative' }}>

              <InteractiveBoard
                c={c}
                fen={currentFen}
                flipped={flipped}
                pieceSet={pieceSet}
                boardTheme={boardTheme}
                selectedSquare={selectedSquare}
                legalMoves={legalMoves}
                lastMove={lastMove}
                onSquareClick={(ri, ci) => { handleSquareClick(ri, ci); }}
                onDragStart={handleDragStart}
                onFlip={() => setFlipped(f => !f)}
                onGoTo={goTo}
                setArrows={setArrows}
                setHighlightedSquares={setHighlightedSquares}
                setPieceSet={setPieceSet}
                setBoardTheme={setBoardTheme}
                arrows={[
                  ...(showEngine && sfEval?.bestMoveUci ? [{ from: sfEval.bestMoveUci.slice(0, 2), to: sfEval.bestMoveUci.slice(2, 4), color: '#3B9ED6' }] : []),
                  ...arrows,
                ]}
                highlightedSquares={highlightedSquares}
                dragFrom={dragFrom}
                dragPos={dragPos}
                dragPiece={dragPiece}
                hoveredSquare={hoveredSquare}
                setHoveredSquare={setHoveredSquare}
                pendingPromotion={pendingPromotion}
                onPromotionChoice={handlePromotionChoice}
                setPendingPromotion={setPendingPromotion}
                positions={positions}
                moveIndex={moveIndex}
                heatmapData={heatmapData}
                threatData={threatData}
                showEvalBar={showEngine && activeMode === 'free'}
                evalPercent={evalPercent}
                materialAdvantage={evalDisplay}
              />
            </Box>

            <Box sx={{ flex: '0 0 280px', display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>

              {/* Stockfish engine toggle */}
              {activeMode === 'free' && (
                <Box
                  onClick={() => setShowEngine(e => !e)}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1.2, px: 2.5, py: 1.2,
                    borderRadius: '16px', cursor: 'pointer', userSelect: 'none',
                    bgcolor: showEngine ? `${c.blue}12` : `${c.textMuted}06`,
                    border: `1px solid ${showEngine ? c.blue + '30' : c.textMuted + '10'}`,
                    transition: 'all 0.2s ease',
                    '&:hover': { bgcolor: showEngine ? `${c.blue}18` : `${c.textMuted}10` },
                  }}
                >
                  <Box sx={{
                    width: 32, height: 18, borderRadius: '9px', position: 'relative',
                    bgcolor: showEngine ? c.blue : `${c.textMuted}40`,
                    transition: 'background-color 0.2s ease',
                  }}>
                    <Box sx={{
                      width: 14, height: 14, borderRadius: '50%', bgcolor: '#fff',
                      position: 'absolute', top: 2,
                      left: showEngine ? 16 : 2,
                      transition: 'left 0.2s ease',
                    }} />
                  </Box>
                  <Typography sx={{ color: showEngine ? c.textDark : c.textMuted, fontSize: '0.82rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                    Stockfish
                  </Typography>
                </Box>
              )}

              {/* Stockfish best move hint */}
              {showEngine && sfEval && sfEval.bestMove && (
                <Box sx={{ px: 2.5, py: 1.5, borderRadius: '16px', bgcolor: `${c.blue}08`, border: `1px solid ${c.blue}15` }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={c.blue} strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10" /><path d="M12 16v-4" /><path d="M12 8h.01" /></svg>
                      <Typography sx={{ color: c.textMuted, fontSize: '0.82rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Stockfish</Typography>
                    </Box>
                    <Typography sx={{ color: c.textDark, fontSize: '0.95rem', fontWeight: 700 }}>
                      {typeof evalDisplay === 'string' ? evalDisplay : (parseFloat(evalDisplay) >= 0 ? '+' : '') + evalDisplay}
                    </Typography>
                  </Box>
                  <Typography sx={{ color: c.blue, fontSize: '1.1rem', fontWeight: 800, mt: 0.5 }}>
                    Best: {sfEval.bestMove}
                  </Typography>
                </Box>
              )}
              {showEngine && sfLoading && !sfEval && (
                <Box sx={{ px: 2.5, py: 1.5, borderRadius: '16px', bgcolor: `${c.textMuted}06`, border: `1px solid ${c.textMuted}10` }}>
                  <Typography sx={{ color: c.textMuted, fontSize: '0.88rem' }}>Analyzing position...</Typography>
                </Box>
              )}

              {/* Turn indicator + Move counter */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 2, borderRadius: '16px', bgcolor: `${c.textMuted}06`, border: `1px solid ${c.textMuted}10` }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: turnColor === 'w' ? '#ECE2DB' : '#111115' }} />
                  <Typography sx={{ color: c.textDark, fontSize: '1.1rem', fontWeight: 700 }}>
                    {isCheckmate ? (turnColor === 'w' ? 'Black wins' : 'White wins') : isStalemate ? 'Stalemate' : isDraw ? 'Draw' : isCheck ? (turnColor === 'w' ? 'White in check' : 'Black in check') : turnColor === 'w' ? 'White to move' : 'Black to move'}
                  </Typography>
                </Box>
                <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', fontWeight: 600 }}>
                  Move {fullMoveNumber}
                </Typography>
              </Box>

              {/* Opening detection */}
              {detectedOpening && (
                <Box sx={{ px: 2.5, py: 2, borderRadius: '16px', bgcolor: `${c.accent}06`, border: `1px solid ${c.accent}12` }}>
                  <Typography sx={{ color: c.textMuted, fontSize: '0.8rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', mb: 0.5 }}>Opening</Typography>
                  <Typography sx={{ color: c.textDark, fontSize: '1.25rem', fontWeight: 800 }}>{openingName || detectedOpening}</Typography>
                </Box>
              )}

              {/* Material count */}
              <Box sx={{ px: 2.5, py: 2, borderRadius: '16px', bgcolor: `${c.textMuted}06`, border: `1px solid ${c.textMuted}10` }}>
                <Typography sx={{ color: c.textMuted, fontSize: '0.8rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', mb: 1.5 }}>Captured Pieces</Typography>
                {/* White's captures (black pieces taken) */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.8 }}>
                  <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: '#ECE2DB', flexShrink: 0 }} />
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.3, minHeight: 28, alignItems: 'center' }}>
                    {['q', 'r', 'b', 'n', 'p'].map(type =>
                      Array(materialCount.captured.w[type]).fill(null).map((_, i) => (
                        <Box key={`wc-${type}-${i}`} component="img" src={svgPieceUrl(pieceSet, `b${type.toUpperCase()}`)} alt="" sx={{ width: 26, height: 26, opacity: 0.7 }} />
                      ))
                    )}
                    {materialCount.advantage > 0 && <Typography sx={{ color: c.textMuted, fontSize: '0.95rem', fontWeight: 700, ml: 0.5 }}>+{materialCount.advantage}</Typography>}
                  </Box>
                </Box>
                {/* Black's captures (white pieces taken) */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                  <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: '#111115', flexShrink: 0 }} />
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.3, minHeight: 28, alignItems: 'center' }}>
                    {['q', 'r', 'b', 'n', 'p'].map(type =>
                      Array(materialCount.captured.b[type]).fill(null).map((_, i) => (
                        <Box key={`bc-${type}-${i}`} component="img" src={svgPieceUrl(pieceSet, `w${type.toUpperCase()}`)} alt="" sx={{ width: 26, height: 26, opacity: 0.7 }} />
                      ))
                    )}
                    {materialCount.advantage < 0 && <Typography sx={{ color: c.textMuted, fontSize: '0.95rem', fontWeight: 700, ml: 0.5 }}>+{Math.abs(materialCount.advantage)}</Typography>}
                  </Box>
                </Box>
              </Box>

              {/* Move history (compact) */}
              <Box sx={{ px: 2.5, py: 2, borderRadius: '16px', bgcolor: `${c.textMuted}06`, border: `1px solid ${c.textMuted}10`, maxHeight: { xs: 180, md: 260 }, overflow: 'auto' }}>
                <Typography sx={{ color: c.textMuted, fontSize: '0.8rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', mb: 1.5 }}>Moves</Typography>
                {movePairs.length === 0 ? (
                  <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', fontStyle: 'italic' }}>Play a move to begin...</Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {movePairs.map((pair) => (
                      <Box key={pair.num} sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                        <Typography sx={{ color: `${c.textMuted}80`, fontSize: '0.95rem', fontWeight: 600 }}>{pair.num}.</Typography>
                        <Box onClick={() => { goTo(pair.whiteIdx); playSound(getSoundType(pair.white)); }} sx={{ px: 1, py: 0.3, borderRadius: '6px', cursor: 'pointer', bgcolor: moveIndex === pair.whiteIdx ? `${c.accent}25` : 'transparent', '&:hover': { bgcolor: `${c.accent}12` } }}>
                          <Typography sx={{ color: moveIndex === pair.whiteIdx ? c.accent : c.textDark, fontSize: '1rem', fontWeight: moveIndex === pair.whiteIdx ? 700 : 500 }}>{pair.white.san}</Typography>
                        </Box>
                        {pair.black && (
                          <Box onClick={() => { goTo(pair.blackIdx); playSound(getSoundType(pair.black)); }} sx={{ px: 1, py: 0.3, borderRadius: '6px', cursor: 'pointer', bgcolor: moveIndex === pair.blackIdx ? `${c.accent}25` : 'transparent', '&:hover': { bgcolor: `${c.accent}12` } }}>
                            <Typography sx={{ color: moveIndex === pair.blackIdx ? c.accent : c.textDark, fontSize: '1rem', fontWeight: moveIndex === pair.blackIdx ? 700 : 500 }}>{pair.black.san}</Typography>
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>

              {/* PGN Export */}
              {history.length > 0 && (
                <Box onClick={() => { generatePGN(); setPgnCopied(true); setTimeout(() => setPgnCopied(false), 2000); }} sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.2,
                  px: 2.5, py: 1.5, borderRadius: '12px', cursor: 'pointer',
                  bgcolor: pgnCopied ? `${c.accent}15` : `${c.textMuted}06`,
                  border: `1px solid ${pgnCopied ? c.accent : c.textMuted}12`,
                  transition: 'all 0.2s ease',
                  '&:hover': { bgcolor: `${c.accent}10`, borderColor: `${c.accent}25` },
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={pgnCopied ? c.accent : c.textMuted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    {pgnCopied ? <><polyline points="20 6 9 17 4 12" /></> : <><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" /></>}
                  </svg>
                  <Typography sx={{ color: pgnCopied ? c.accent : c.textMuted, fontSize: '1rem', fontWeight: 600 }}>
                    {pgnCopied ? 'PGN Copied!' : 'Copy PGN'}
                  </Typography>
                </Box>
              )}

              {/* Board tools (toggles) */}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Box onClick={() => setShowHeatmap(h => !h)} sx={{
                  display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.2, borderRadius: '12px', cursor: 'pointer',
                  bgcolor: showHeatmap ? `${c.accent}15` : `${c.textMuted}06`,
                  border: `1.5px solid ${showHeatmap ? c.accent : 'transparent'}`,
                  color: showHeatmap ? c.accent : c.textMuted,
                  transition: 'all 0.15s ease',
                  '&:hover': { bgcolor: showHeatmap ? `${c.accent}20` : `${c.textMuted}10` },
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="3" y="3" width="18" height="18" rx="2" /><rect x="7" y="7" width="4" height="4" opacity="0.5" /><rect x="13" y="13" width="4" height="4" opacity="0.3" /></svg>
                  <Typography sx={{ fontSize: '0.95rem', fontWeight: 600 }}>Heatmap</Typography>
                </Box>
                <Box onClick={() => setShowThreats(t => !t)} sx={{
                  display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.2, borderRadius: '12px', cursor: 'pointer',
                  bgcolor: showThreats ? '#E4446E15' : `${c.textMuted}06`,
                  border: `1.5px solid ${showThreats ? '#E4446E' : 'transparent'}`,
                  color: showThreats ? '#E4446E' : c.textMuted,
                  transition: 'all 0.15s ease',
                  '&:hover': { bgcolor: showThreats ? '#E4446E20' : `${c.textMuted}10` },
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
                  <Typography sx={{ fontSize: '0.95rem', fontWeight: 600 }}>Threats</Typography>
                </Box>
              </Box>

              {/* Move classification */}
              {moveIndex > 0 && history[moveIndex - 1] && (
                <Box sx={{ px: 2.5, py: 2, borderRadius: '16px', bgcolor: `${c.textMuted}06`, border: `1px solid ${c.textMuted}10` }}>
                  <Typography sx={{ color: c.textMuted, fontSize: '0.8rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', mb: 1 }}>Last Move</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, flexWrap: 'wrap' }}>
                    <Typography sx={{ color: c.textDark, fontSize: '1.25rem', fontWeight: 800 }}>{history[moveIndex - 1].san}</Typography>
                    {classifyMove(history[moveIndex - 1]).map(tag => (
                      <Box key={tag} sx={{
                        px: 1.2, py: 0.3, borderRadius: '8px', fontSize: '0.85rem', fontWeight: 700,
                        bgcolor: tag === 'Check' ? '#E4446E18' : tag === 'Checkmate' ? '#E4446E25' : tag === 'Capture' ? '#DE734518' : tag === 'Castle' ? `${c.accent}15` : tag === 'Development' ? '#00A5C415' : `${c.textMuted}10`,
                        color: tag === 'Check' ? '#E4446E' : tag === 'Checkmate' ? '#E4446E' : tag === 'Capture' ? '#DE7345' : tag === 'Castle' ? c.accent : tag === 'Development' ? '#00A5C4' : c.textMuted,
                      }}>
                        {tag}
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              {/* Game over */}
              {isGameOver && (
                <Box sx={{ px: 2.5, py: 2, borderRadius: '16px', bgcolor: isCheckmate ? `${c.accent}08` : `${c.textMuted}06`, border: `1px solid ${isCheckmate ? c.accent : c.textMuted}15` }}>
                  <Typography sx={{ color: c.textDark, fontSize: '1.15rem', fontWeight: 700 }}>
                    {isCheckmate ? 'Checkmate!' : isStalemate ? 'Stalemate' : isThreefoldRepetition ? 'Threefold Repetition' : isInsufficientMaterial ? 'Insufficient Material' : isFiftyMoveRule ? '50-Move Rule' : 'Draw'}
                  </Typography>
                  <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', mt: 0.5 }}>
                    {isCheckmate ? `${turnColor === 'w' ? 'Black' : 'White'} wins the game.`
                      : isStalemate ? 'Neither side can win — the game is drawn.'
                      : isThreefoldRepetition ? 'The same position occurred three times — draw.'
                      : isInsufficientMaterial ? 'Neither side has enough pieces to checkmate — draw.'
                      : isFiftyMoveRule ? '50 moves without a capture or pawn move — draw.'
                      : 'The game has ended in a draw.'}
                  </Typography>
                </Box>
              )}

            </Box>
          </Box>
        )}

          {/* ── GAME ANALYSIS mode ── */}
          {activeMode === 'analysis' && (
            <GameAnalysisPanel c={c} pieceSet={pieceSet} boardTheme={boardTheme} setPieceSet={setPieceSet} setBoardTheme={setBoardTheme} />
          )}

          {/* ── PUNISHMENT mode ── */}
          {activeMode === 'punishment' && (
            <PunishmentPanel c={c} pieceSet={pieceSet} boardTheme={boardTheme} setPieceSet={setPieceSet} setBoardTheme={setBoardTheme} />
          )}

          {/* ── OPENING TRAINER mode ── */}
          {activeMode === 'openings' && (
            <OpeningTrainerPanel c={c} pieceSet={pieceSet} boardTheme={boardTheme} setPieceSet={setPieceSet} setBoardTheme={setBoardTheme} />
          )}

          {/* ── FIND CHECKMATE mode ── */}
          {activeMode === 'checkmate' && (
            <FindCheckmatePanel c={c} pieceSet={pieceSet} boardTheme={boardTheme} setPieceSet={setPieceSet} setBoardTheme={setBoardTheme} />
          )}

          {/* ── BLUNDER REVIEW mode ── */}
          {activeMode === 'blunders' && (
            <BlunderReviewPanel c={c} pieceSet={pieceSet} boardTheme={boardTheme} setPieceSet={setPieceSet} setBoardTheme={setBoardTheme} />
          )}

          {/* ── Coming Soon modes ── */}
          {activeMode !== 'free' && activeMode !== 'punishment' && activeMode !== 'openings' && activeMode !== 'checkmate' && activeMode !== 'blunders' && activeMode !== 'analysis' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: { xs: 8, md: 12 }, textAlign: 'center' }}>
              <Box sx={{ width: 80, height: 80, borderRadius: '24px', bgcolor: `${c.accent}10`, border: `1.5px solid ${c.accent}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3, color: c.accent }}>
                <Box sx={{ transform: 'scale(2.2)', display: 'flex' }}>{currentMode.icon}</Box>
              </Box>
              <Typography sx={{ color: c.textDark, fontSize: '1.6rem', fontWeight: 800, mb: 1 }}>{currentMode.label}</Typography>
              <Typography sx={{ color: c.textMuted, fontSize: '1.1rem', lineHeight: 1.8, maxWidth: 480, mb: 3 }}>{currentMode.description}</Typography>
              <Box sx={{ px: 3, py: 1.2, borderRadius: '12px', bgcolor: `${c.accent}10`, border: `1.5px solid ${c.accent}25` }}>
                <Typography sx={{ color: c.accent, fontSize: '0.85rem', fontWeight: 700 }}>Coming Soon</Typography>
              </Box>
            </Box>
          )}

        </Box>
      </Box>
    </Box>
  );
}
